import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu as IconaMenu, Search, Bell, Plus } from 'lucide-react';
import api from '../../services/api';
import { schermataDi, siApre } from '../../navigazione/schermate';
import { apriDestinazione } from '../../navigazione/useAzione';
import { periodoAvvisi } from '../../utils/periodoAvvisi';

/**
 * BarraInAlto — la barra in alto dello stile «Console» (fase 1, punto 3;
 * mockup «Main»: «LAVORO / OGGI», «Controlli 21/22», la campanella col
 * numero, «Nuovo»). La monta il Layout, sopra la pagina.
 *
 * DA SINISTRA:
 *   - il pulsante del menu, solo sotto gli 820 px (il menu e' un cassetto);
 *   - DOVE SI E': gruppo, schermata da cui si arriva, schermata. Solo
 *     etichette della tabella navigazione/schermate.js, MAI un dato: il
 *     nome di un paziente nel percorso finirebbe nelle fotografie e nelle
 *     trascrizioni (CLAUDE.md, 5-bis). Sulla cartella c'e' scritto
 *     «Cartella», non di chi;
 *   - il posto degli avvisi che arrivano da soli (postiAvvisi.js): da tre
 *     ritardi in su, «N pazienti in ritardo»;
 *   - «Controlli», solo a chi apre la loro schermata (oggi
 *     l'amministrazione, /stato-sistema): quanti controlli automatici sono
 *     in ordine su quanti, dal server (GET /api/stato-sistema/controlli,
 *     services/controlli.js: solo numeri). Porta allo stato del sistema
 *     (Alex, 2/10/2026, domanda 2);
 *   - LA CAMPANELLA degli avvisi: il numero dei promemoria arrivati, lo
 *     stesso elenco che il Promemoria dell'angolo mostra (lo comunica
 *     AlertNotifier al Layout). Un clic apre il Promemoria; senza
 *     promemoria arrivati apre la schermata dei Promemoria;
 *   - la lente, solo sotto gli 820 px: la barra comandi (sopra, la apre il
 *     campo «Cerca o esegui» del menu, o Ctrl K);
 *   - «NUOVO»: le azioni di creazione del ruolo, azioniDi(utente) della
 *     tabella, gia' filtrate con la guardia delle rotte. Si aprono con
 *     apriDestinazione (navigazione/useAzione.js), la stessa regola della
 *     barra comandi: sulla pagina dove si e' gia' il router non chiede
 *     del lavoro non salvato, e la domanda la fa lei.
 *
 * PROPS (le passa il Layout)
 *   percorso       percorsoDi(schermata, utente): [{ etichetta, percorso? }];
 *                  un pezzo senza percorso (il gruppo, una schermata che
 *                  l'utente non apre, un dettaglio) resta un'etichetta
 *   utente         l'utente collegato
 *   azioni         azioniDi(utente)
 *   contatori      useContatori(): oggi la barra non li usa, li mostra il
 *                  menu accanto alle voci
 *   onApriComandi  apre la barra comandi
 *   postoAvvisi    il ref callback del posto degli avvisi nella barra
 *   avvisi         { quanti, onApri }: la campanella
 *   menuAperto     il cassetto del menu e' aperto (sotto gli 820 px)
 *   onApriMenu     apre e chiude il cassetto
 *
 * La classe `.header` resta: PilaAvvisi apre il suo pannello sotto
 * `.header`, e le prove (provaAngoloAvvisi, verificaRotte) la misurano.
 */

/* Ogni quanto si rilegge lo stato dei controlli. I controlli guardano
   cose che cambiano nell'arco della giornata (una fattura scartata, un
   lotto da leggere): cinque minuti bastano, e 23 funzioni costano 36 ms.
   Passa dall'orologio degli avvisi (utils/periodoAvvisi.js), come
   useContatori: e' una lettura periodica che si sveglia insieme al
   controllo della versione (utils/versioneInLinea.js), e le prove dei
   percorsi le accorciano tutte insieme (revisione dell'unione beta-unico,
   3/10/2026). Senza l'oggetto delle prove: cinque minuti. */
const OGNI_MS = 5 * 60 * 1000;
const CHIAVI_CONTROLLI = ['totale', 'in_ordine', 'da_guardare', 'soglia_non_valida'];

/**
 * Lo stato dei controlli, solo se `attivo`. Tre esiti DIVERSI, e la barra
 * li deve distinguere: null (non ancora arrivato), { errore: true } (il
 * server non ha risposto, o ha risposto qualcosa che non sono numeri), i
 * numeri. Una risposta strana non diventa «tutto in ordine».
 */
function useStatoControlli(attivo) {
    const [stato, setStato] = useState(null);
    useEffect(() => {
        if (!attivo) { setStato(null); return undefined; }
        let vivo = true;
        const leggi = () => api.get('/stato-sistema/controlli')
            .then((r) => {
                const d = r && r.data && r.data.data;
                const numeri = d && CHIAVI_CONTROLLI.every((k) => Number.isInteger(d[k]) && d[k] >= 0);
                if (vivo) setStato(numeri ? d : { errore: true });
            })
            .catch(() => { if (vivo) setStato({ errore: true }); });
        leggi();
        const t = setInterval(leggi, periodoAvvisi(OGNI_MS));
        return () => { vivo = false; clearInterval(t); };
    }, [attivo]);
    return stato;
}

/* Il colore della pillola: una funzione e non un ternario annidato nel
   JSX, che il controllo dei nomi di classe non saprebbe leggere. */
function classeControlli(s) {
    if (!s) return 'guscio-controlli--attesa';
    if (s.errore) return 'guscio-controlli--errore';
    return s.in_ordine === s.totale ? 'guscio-controlli--bene' : 'guscio-controlli--guarda';
}

function spiegaControlli(s) {
    if (!s) return 'Controlli automatici: lettura in corso';
    if (s.errore) return 'Controlli automatici: lo stato non si è potuto leggere. Apri lo stato del sistema';
    const pezzi = [`Controlli automatici: ${s.in_ordine} in ordine su ${s.totale}`];
    if (s.da_guardare) pezzi.push(`${s.da_guardare} con qualcosa da guardare`);
    if (s.soglia_non_valida) pezzi.push(`${s.soglia_non_valida} con la soglia non valida`);
    return pezzi.join(', ');
}

export default function BarraInAlto({
    percorso = [], utente, azioni = [], onApriComandi, postoAvvisi,
    avvisi, menuAperto = false, onApriMenu,
}) {
    const navigate = useNavigate();
    /* La posizione intera, non il solo percorso: «Nuovo» sulla pagina dove
       si e' gia' sostituisce la voce e ne tiene ricerca e ancora (il giorno
       dell'agenda; apriDestinazione). */
    const location = useLocation();
    const vedeControlli = Boolean(utente) && siApre(schermataDi('/stato-sistema'), utente);
    const controlli = useStatoControlli(vedeControlli);
    const [nuovoAperto, setNuovoAperto] = useState(false);
    const nuovo = useRef(null);

    /* «Nuovo» si chiude a un clic fuori e con Esc: e' un elenco aperto da
       chi lo voleva vedere, non deve restare davanti alla pagina. */
    useEffect(() => {
        if (!nuovoAperto) return undefined;
        const fuori = (e) => { if (!nuovo.current?.contains(e.target)) setNuovoAperto(false); };
        const esc = (e) => { if (e.key === 'Escape') setNuovoAperto(false); };
        document.addEventListener('mousedown', fuori);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fuori);
            document.removeEventListener('keydown', esc);
        };
    }, [nuovoAperto]);

    const ultimo = percorso.length - 1;
    const quanti = (avvisi && avvisi.quanti) || 0;
    const nomeAvvisi = quanti > 0
        ? `Avvisi: ${quanti} da vedere`
        : 'Avvisi: nessuno da vedere. Apri i promemoria';

    return (
        <header className="header">
            <button type="button" className="guscio-apri-menu" aria-controls="guscio-menu"
                    aria-expanded={menuAperto} aria-label="Menu" title="Menu" onClick={onApriMenu}>
                <IconaMenu size={18} strokeWidth={1.9} aria-hidden="true" />
            </button>

            <nav className="guscio-percorso" aria-label="Dove sei">
                <ol>
                    {percorso.map((p, i) => (
                        <li key={`${i}-${p.etichetta}`}>
                            {p.percorso && i < ultimo ? (
                                <button type="button" className="guscio-percorso-voce"
                                        onClick={() => navigate(p.percorso)}>{p.etichetta}</button>
                            ) : (
                                <span className="guscio-percorso-voce"
                                      aria-current={i === ultimo ? 'page' : undefined}>{p.etichetta}</span>
                            )}
                        </li>
                    ))}
                </ol>
            </nav>

            {/* Da tre ritardi (o attese) in su, il loro pulsante sta qui:
                nella barra non copre niente. */}
            <div className="rm-barra-avvisi" ref={postoAvvisi} />

            {vedeControlli && (
                <button type="button" className={`guscio-controlli ${classeControlli(controlli)}`}
                        onClick={() => navigate('/stato-sistema')}
                        title={spiegaControlli(controlli)} aria-label={spiegaControlli(controlli)}>
                    <span className="guscio-controlli-punto" aria-hidden="true" />
                    <span className="guscio-controlli-nome">Controlli</span>
                    {controlli && !controlli.errore && (
                        <span className="rm-numeri">{controlli.in_ordine}/{controlli.totale}</span>
                    )}
                    {controlli && controlli.errore && <span className="rm-numeri">?</span>}
                </button>
            )}

            <button type="button" className="guscio-avvisi" onClick={avvisi && avvisi.onApri}
                    title={nomeAvvisi} aria-label={nomeAvvisi}>
                <Bell size={17} strokeWidth={1.8} aria-hidden="true" />
                {quanti > 0 && <span className="guscio-avvisi-numero rm-numeri" aria-hidden="true">{quanti}</span>}
            </button>

            <button type="button" className="guscio-cerca-barra" onClick={onApriComandi}
                    title="Cerca o esegui" aria-label="Cerca o esegui">
                <Search size={17} strokeWidth={1.9} aria-hidden="true" />
            </button>

            {azioni.length > 0 && (
                <div className="guscio-nuovo" ref={nuovo}>
                    <button type="button" className="guscio-nuovo-pulsante" aria-expanded={nuovoAperto}
                            aria-haspopup="true" aria-label="Nuovo" onClick={() => setNuovoAperto((x) => !x)}>
                        <Plus size={15} strokeWidth={2.2} aria-hidden="true" />
                        <span className="guscio-nuovo-etichetta">Nuovo</span>
                    </button>
                    {nuovoAperto && (
                        <ul className="guscio-nuovo-elenco">
                            {azioni.map((a) => (
                                <li key={a.codice}>
                                    <button type="button" className="guscio-nuovo-voce" data-azione={a.codice}
                                            onClick={() => { setNuovoAperto(false); apriDestinazione(navigate, location, a); }}>
                                        {a.etichetta}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </header>
    );
}
